/* eslint-disable jsx-a11y/heading-has-content */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useRef, useState } from "react";
import { BiCloudUpload } from "react-icons/bi";
import { FaRegCheckCircle } from "react-icons/fa";
import { FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import * as Styles from "./dragNDrop.styles";
import { MIN_DIMENSION, MAX_UPLOAD_SIZE } from "../FileUploadModal/utils";

interface DragNDropProps {
  onFilesSelected: (file: File) => void;
  width?: string;
  height?: string;
  removeIcon?: boolean;
  accept: string;
  onChangeManual?: (file: File) => void;
  onRemoveFile?: () => void;
}

export const DragNDrop = ({
  onFilesSelected,
  width,
  height,
  removeIcon,
  accept,
  onChangeManual,
  onRemoveFile,
}: DragNDropProps) => {
  const [file, setFile] = useState<any>(null);
  const [fileManual, setFileManual] = useState<any>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const formatAccept = (): string => {
    const mimeTypes: string[] = accept.split(",");

    const extensions = mimeTypes.map((mimeType) => {
      const trimmedMimeType = mimeType.trim();
      if (trimmedMimeType.includes("/")) {
        const parts = trimmedMimeType.split("/");
        return `.${parts[1]}`;
      }

      return trimmedMimeType;
    });

    const formattedString = extensions
      .join(", ")
      .replace(/, ([^,]*)$/, " e $1");

    return formattedString;
  };

  const formats = formatAccept();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const acceptedTypes =
        accept?.split(",").map((type: string) => type.trim()) ?? [];
      const isValidType = acceptedTypes.some(
        (type) =>
          file.type === type ||
          (type.includes("*") && file.name.endsWith(type.replace("*", "")))
      );

      if (!isValidType) {
        toast.error("Formato de arquivo inválido");
      } else if (file.size > MAX_UPLOAD_SIZE) {
        toast.error(
          `Tamanho do arquivo deve ser menor que ${
            MAX_UPLOAD_SIZE / 1024 / 1024
          }MB`
        );
      } else {
        setFileManual(file);
        onChangeManual?.(file);
      }
    }
  };

  const handleDrop = (event: any) => {
    event.preventDefault();
    setIsDragging(false);
    const droppedFile = event.dataTransfer.files[0];

    if (!droppedFile) {
      toast.error("Ocorreu um erro ao carregar o arquivo");
      return;
    }

    const acceptedTypes = accept.split(",").map((type: string) => type.trim());

    if (!acceptedTypes.includes(droppedFile.type)) {
      toast.error("Formato de arquivo inválido");
      return;
    }

    if (droppedFile.size > MAX_UPLOAD_SIZE) {
      toast.error(
        `Tamanho do arquivo deve ser menor que ${
          MAX_UPLOAD_SIZE / 1024 / 1024
        }MB`
      );
      return;
    }

    const reader = new FileReader();

    reader.onload = (e: any) => {
      if (droppedFile.type.startsWith("image")) {
        const img = new Image();
        img.src = e.target.result;
        img.onload = () => {
          URL.revokeObjectURL(img.src);

          if (img.width < MIN_DIMENSION || img.height < MIN_DIMENSION) {
            toast.error(
              `Imagem deve ter um tamanho de pelo menos ${MIN_DIMENSION}x${MIN_DIMENSION}.`
            );
            return;
          }

          setFile(droppedFile);
        };
      } else {
        setFile(droppedFile);
      }
    };

    reader.readAsDataURL(droppedFile);
  };

  const handleDragOver = (event: any) => {
    event.preventDefault();
  };

  const handleDragEnter = (event: any) => {
    event.preventDefault();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event: any) => {
    event.preventDefault();
    if (
      event.relatedTarget &&
      event.currentTarget.contains(event.relatedTarget)
    ) {
      return;
    }
    setIsDragging(false);
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const clearFile = () => {
    setFile(null);
    setFileManual(null);
    onRemoveFile?.();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (file) onFilesSelected(file);
  }, [file]);

  return (
    <Styles.Container>
      <section className="drag-drop" style={{ width, height }}>
        <div
          className={`document-uploader ${
            file ? "upload-box active" : "upload-box"
          } ${isDragging ? "dragging" : ""}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
        >
          <div className="upload-info">
            {removeIcon ? <h1 style={{ paddingTop: 18 }} /> : <BiCloudUpload />}
            <div>
              <p>
                {"Arraste e solte o arquivo aqui"}
                {!!onChangeManual && ", ou "}
                {!!onChangeManual && (
                  <span onClick={openFilePicker}>acesse a pasta</span>
                )}
              </p>
              <p>Formatos suportados: {formats ?? "Qualquer"}</p>
            </div>
          </div>

          {(file || fileManual) && (
            <div className="file-list">
              <div className="file-list__container">
                <div className="file-item">
                  <div className="file-info">
                    <p>{file?.name ?? fileManual?.name}</p>
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <FaRegCheckCircle color="green" />
                    {!!onRemoveFile && (
                      <FiTrash2
                        onClick={clearFile}
                        color="red"
                        style={{ cursor: "pointer" }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
          accept={accept}
        />
      </section>
    </Styles.Container>
  );
};
